import { createFileRoute, Link } from "@tanstack/react-router";
import { PortalAccountMenu } from "@/components/PortalAccountMenu";
import { useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Beaker,
  Check,
  Clock,
  ClipboardCheck,
  FileSearch,
  FolderOpen,
  GitBranch,
  Layers,
  Lightbulb,
  ShieldCheck,
  Target,
  Wrench,
} from "lucide-react";
import {
  MANAGEMENT_STATUSES,
  type ManagementStatus,
  type ProblemStageState,
} from "@/lib/problem-record-data";
import { saveProblemRecord, useProblemRecords } from "@/lib/problem-record-store";
import { STAGE_REQUIREMENTS, setManagementStatus } from "@/lib/problem-record-workflow";
import { StageWorkspaceDialog } from "@/components/StageWorkspaceDialog";
import { claimsForComponent, currency } from "@/lib/top5-data";
import { pfmeaControl, readAcross } from "@/lib/warranty-data";
import { toast } from "sonner";
import { LiveProblemRecordDetail } from "@/components/LiveProblemRecords";
import { usePortalRuntime } from "@/lib/portal-runtime";

export const Route = createFileRoute("/problem-record/$id")({
  head: ({ params }) => ({
    meta: [
      { title: `Problem Record ${params.id} — Broadview Warranty Intelligence` },
      {
        name: "description",
        content:
          "Closed-loop problem record: warranty evidence, 8D, root cause, corrective action, engineering change, PFMEA, validation, effectiveness and read-across.",
      },
      { property: "og:title", content: `Problem Record ${params.id} — Broadview` },
      {
        property: "og:description",
        content: "Digital quality case file with full warranty-to-prevention traceability.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PortalProblemRecordPage,
});

const stateTone: Record<ProblemStageState, string> = {
  Complete: "border-success/40 bg-success/10 text-success",
  Active: "border-primary/50 bg-primary/15 text-primary",
  Pending: "border-border bg-muted text-muted-foreground",
};

const mgmtTone: Record<ManagementStatus, string> = {
  Draft: "border-border bg-muted text-muted-foreground",
  Active: "border-primary/50 bg-primary/15 text-primary",
  "On Hold": "border-warning/40 bg-warning/10 text-warning",
  Escalated: "border-destructive/40 bg-destructive/10 text-destructive",
  Closed: "border-success/40 bg-success/10 text-success",
};

const TABS = [
  { key: "overview", label: "Overview", icon: Layers },
  { key: "claims", label: "Claims & Evidence", icon: FileSearch },
  { key: "8d", label: "8D / Problem Solving", icon: ClipboardCheck },
  { key: "root-cause", label: "Root Cause", icon: Target },
  { key: "corrective", label: "Corrective Actions", icon: Wrench },
  { key: "eco", label: "Engineering Change", icon: GitBranch },
  { key: "pfmea", label: "PFMEA & Control Plan", icon: ShieldCheck },
  { key: "validation", label: "Validation", icon: Beaker },
  { key: "effectiveness", label: "Effectiveness", icon: Activity },
  { key: "read-across", label: "Lessons / Read-Across", icon: Lightbulb },
  { key: "audit", label: "Audit / Activity", icon: FolderOpen },
] as const;

type TabKey = (typeof TABS)[number]["key"];

function PortalProblemRecordPage() {
  const { id } = Route.useParams();
  const { mode } = usePortalRuntime();
  return mode === "live" ? <LiveProblemRecordDetail id={id} /> : <ProblemRecordPrototypePage />;
}

function ProblemRecordPrototypePage() {
  const { id } = Route.useParams();
  const records = useProblemRecords();
  const record = records.find((r) => r.id.toLowerCase() === id.toLowerCase());
  const [tab, setTab] = useState<TabKey>("overview");
  const [stageKey, setStageKey] = useState<string | null>(null);

  if (!record) return <NoRecord id={id} />;

  const pfmea = pfmeaControl.find((p) => p.caseId === record.linkedCaseId);
  const ra = readAcross.find((r) => r.sourceCaseId === record.linkedCaseId);
  const claims = record.linkedClaims?.length
    ? record.linkedClaims
    : claimsForComponent(record.component);
  const mgmt: ManagementStatus = record.managementStatus ?? "Active";
  const activeStage =
    record.stages.find((s) => s.state === "Active") ??
    record.stages.find((s) => s.state !== "Complete");

  function changeManagement(next: ManagementStatus) {
    if (next === mgmt || !record) return;
    const reason =
      next === "On Hold"
        ? (window.prompt("Reason for placing this record on hold:") ?? "")
        : undefined;
    saveProblemRecord(setManagementStatus(record, next, record.owner, reason || undefined));
    toast.success(`Management status: ${next}`, { description: "Technical stage unchanged" });
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-3 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg"
              style={{ background: "var(--gradient-primary)" }}
            >
              <FolderOpen className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold">Problem Record</div>
              <div className="text-xs text-muted-foreground">Closed-loop quality case file</div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/problem-records"
              className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/15"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Problem Record Registry
            </Link>
            <Link
              to="/top5"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              Top 5 Intelligence
            </Link>

            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              Dashboard
            </Link>
            <PortalAccountMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] space-y-6 px-6 py-8">
        {/* Record header */}
        <section className="rounded-xl border border-border bg-card p-6">
          <div className="flex flex-wrap items-start gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded bg-primary/15 px-2 py-0.5 font-semibold text-primary">
                  {record.id}
                </span>
                <span className="rounded border border-destructive/40 bg-destructive/10 px-2 py-0.5 font-medium text-destructive">
                  {record.severity} · Risk {record.riskScore}
                </span>
                <span className={`rounded border px-2 py-0.5 font-medium ${mgmtTone[mgmt]}`}>
                  Management: {mgmt}
                </span>
                <span className="rounded border border-border px-2 py-0.5 text-muted-foreground">
                  Stage:{" "}
                  {activeStage
                    ? `${activeStage.order}. ${activeStage.label}`
                    : "All stages complete"}
                </span>
                <span className="rounded border border-border px-2 py-0.5 text-muted-foreground">
                  Source: {record.source}
                </span>
                {record.holdReason && mgmt === "On Hold" && (
                  <span className="rounded border border-warning/40 bg-warning/10 px-2 py-0.5 text-warning">
                    Hold: {record.holdReason}
                  </span>
                )}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Set management status
                </span>
                {MANAGEMENT_STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => changeManagement(s)}
                    className={`rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                      s === mgmt
                        ? mgmtTone[s]
                        : "border-border text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <h1 className="mt-2 text-2xl font-semibold tracking-tight">{record.title}</h1>
              <p className="mt-2 max-w-4xl text-sm text-muted-foreground">{record.narrative}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs md:grid-cols-3">
              {[
                { l: "Total claims", v: record.totalClaims.toLocaleString() },
                { l: "Warranty exposure", v: currency(record.warrantyCost) },
                { l: "Claims / 1,000", v: record.claimsPer1000.toFixed(1) },
                { l: "Owner", v: record.owner },
                { l: "Opened", v: record.openedOn },
                { l: "Aging", v: `${record.daysOpen} days open` },
              ].map((k) => (
                <div key={k.l} className="rounded-lg border border-border bg-background p-3">
                  <div className="text-[11px] text-muted-foreground">{k.l}</div>
                  <div className="mt-0.5 text-sm font-semibold">{k.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-4 text-[11px]">
            <Meta label="Affected products" value={record.products.join(", ")} />
            <Meta label="Car lines" value={record.carLines.join(", ")} />
            <Meta label="Component" value={record.component} />
            <Meta label="Supplier" value={record.supplier} />
            <Meta label="Linked 8D" value={record.linkedCaseId} />
            <Meta label="Safety severity" value={record.safetySeverity} />
            {record.linkedFieldActionId && (
              <Link
                to="/recall-safety"
                className="rounded border border-destructive/40 bg-destructive/10 px-2 py-1 font-medium text-destructive hover:bg-destructive/15"
              >
                Field action {record.linkedFieldActionId} →
              </Link>
            )}
          </div>
        </section>

        {/* Closed loop progression */}
        <section className="rounded-xl border border-border bg-card p-5">
          <div className="mb-4 flex flex-wrap items-center gap-2 text-sm font-semibold">
            <Activity className="h-4 w-4 text-primary" /> Closed-loop progression
            <span className="text-xs font-normal text-muted-foreground">
              {record.stages.filter((s) => s.state === "Complete").length} of {record.stages.length}{" "}
              complete
            </span>
            <span className="ml-auto text-xs font-normal text-muted-foreground">
              Select a stage to record evidence, request approval and complete it
            </span>
          </div>
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
            {record.stages.map((s) => {
              const req = STAGE_REQUIREMENTS[s.key];
              const total = req?.fields.filter((f) => !f.optional).length ?? 0;
              const filled = req
                ? req.fields.filter((f) => !f.optional && (s.evidence?.[f.key] ?? "").trim() !== "")
                    .length
                : 0;
              return (
                <button
                  key={s.key}
                  onClick={() => setStageKey(s.key)}
                  className={`rounded-lg border p-3 text-left text-xs transition-colors hover:border-primary/60 ${
                    s.state === "Active"
                      ? "border-primary bg-primary/10"
                      : "border-border bg-background hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold ${stateTone[s.state]}`}
                    >
                      {s.state === "Complete" ? <Check className="h-3 w-3" /> : s.order}
                    </span>
                    <span className="font-medium">{s.label}</span>
                    <span
                      className={`ml-auto rounded border px-1.5 py-0.5 text-[10px] ${stateTone[s.state]}`}
                    >
                      {s.state}
                    </span>
                  </div>
                  <p className="mt-2 line-clamp-3 text-muted-foreground">
                    {s.summary || "No evidence recorded yet."}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                    <span>{s.owner}</span>
                    {s.completedOn ? <span>· {s.completedOn}</span> : null}
                    {s.due ? <span>· due {s.due}</span> : null}
                    <span
                      className={`ml-auto rounded border px-1.5 py-0.5 ${
                        filled === total ? "border-success/40 text-success" : "border-border"
                      }`}
                    >
                      Evidence {filled}/{total}
                    </span>
                    {s.approvalRequired && (
                      <span
                        className={`rounded border px-1.5 py-0.5 ${
                          s.approvalStatus === "Approved"
                            ? "border-success/40 text-success"
                            : "border-warning/40 text-warning"
                        }`}
                      >
                        {s.approvalStatus === "Approved" ? "Approved" : "Approval req."}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Tabs */}
        <section className="flex flex-wrap gap-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                  t.key === tab
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          })}
        </section>

        <section className="rounded-xl border border-border bg-card p-6">
          {tab === "overview" && (
            <div className="grid gap-4 lg:grid-cols-3">
              <Panel title="Warranty signal">
                <p className="text-muted-foreground">{record.spcSignal}</p>
                <p className="mt-2 text-muted-foreground">{record.predictiveSignal}</p>
                <Link
                  to="/top5"
                  className="mt-3 inline-block font-medium text-primary hover:underline"
                >
                  View in Top 5 Intelligence →
                </Link>
              </Panel>
              <Panel title="Exposure">
                <Row l="Total claims" v={record.totalClaims.toLocaleString()} />
                <Row l="Warranty cost" v={currency(record.warrantyCost)} />
                <Row l="Claims / 1,000" v={record.claimsPer1000.toFixed(1)} />
                <Row l="Car lines" v={record.carLines.length.toString()} />
                <Row l="Supplier" v={record.supplier} />
              </Panel>
              <Panel title="Governance">
                <Row l="Owner" v={record.owner} />
                <Row l="Sponsor" v={record.sponsor} />
                <Row l="Opened" v={record.openedOn} />
                <Row l="Days open" v={`${record.daysOpen}`} />
                <Row
                  l="Current stage"
                  v={record.stages.find((s) => s.state === "Active")?.label ?? "—"}
                />
              </Panel>
            </div>
          )}

          {tab === "claims" && (
            <div>
              <SectionTitle
                icon={FileSearch}
                title="Claims & evidence"
                sub={`${claims.length} representative claims of ${record.totalClaims}`}
              />
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full min-w-[900px] text-left text-xs">
                  <thead className="bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">Claim</th>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Car line</th>
                      <th className="px-3 py-2">Failure mode</th>
                      <th className="px-3 py-2 text-right">Cost</th>
                      <th className="px-3 py-2 text-right">Mileage / MIS</th>
                      <th className="px-3 py-2">Narrative</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claims.map((c) => (
                      <tr key={c.id} className="border-t border-border align-top">
                        <td className="px-3 py-2 font-medium">{c.id}</td>
                        <td className="px-3 py-2 text-muted-foreground">{c.date}</td>
                        <td className="px-3 py-2">{c.carLine}</td>
                        <td className="px-3 py-2 text-muted-foreground">{c.failureMode}</td>
                        <td className="px-3 py-2 text-right font-medium">
                          ${c.cost.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right text-muted-foreground">
                          {c.mileage.toLocaleString()} mi · {c.mis} MIS
                        </td>
                        <td className="max-w-md px-3 py-2 text-muted-foreground">{c.complaint}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "8d" && record.eightD.length > 0 && (
            <div>
              <SectionTitle
                icon={ClipboardCheck}
                title={`8D problem solving — ${record.linkedCaseId}`}
                sub="Linked to the active 8D case on the analyst dashboard"
              />
              <div className="grid gap-2 md:grid-cols-2">
                {record.eightD.map((d) => (
                  <div
                    key={d.code}
                    className="rounded-lg border border-border bg-background p-3 text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <span className="rounded bg-muted px-1.5 py-0.5 font-semibold">{d.code}</span>
                      <span className="font-medium">{d.label}</span>
                      <span
                        className={`ml-auto rounded border px-1.5 py-0.5 text-[10px] ${stateTone[d.state]}`}
                      >
                        {d.state}
                      </span>
                    </div>
                    <p className="mt-2 text-muted-foreground">{d.note}</p>
                    <div className="mt-1 text-[10px] text-muted-foreground">Owner: {d.owner}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tab === "8d" && !(record.eightD.length > 0) && (
            <PendingSection
              title="8D problem solving"
              note="No 8D has been initiated for this record yet. Start the 8D from the problem definition stage to open D1–D8."
            />
          )}

          {tab === "root-cause" && record.rootCause.statement && (
            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <SectionTitle
                  icon={Target}
                  title="Root cause statement"
                  sub={record.rootCause.method}
                />
                <p className="rounded-lg border border-border bg-background p-4 text-sm">
                  {record.rootCause.statement}
                </p>
                <div className="mt-3 space-y-2 text-xs">
                  <Panel title="Occurrence cause">
                    <p className="text-muted-foreground">{record.rootCause.occurrence}</p>
                  </Panel>
                  <Panel title="Detection cause">
                    <p className="text-muted-foreground">{record.rootCause.detection}</p>
                  </Panel>
                  <Panel title="Systemic cause">
                    <p className="text-muted-foreground">{record.rootCause.systemic}</p>
                  </Panel>
                </div>
              </div>
              <div>
                <SectionTitle
                  icon={Layers}
                  title="5-Why chain"
                  sub="Verified by teardown metrology"
                />
                <ol className="space-y-2 text-xs">
                  {record.rootCause.fiveWhys.map((w, i) => (
                    <li key={i} className="rounded-lg border border-border bg-background p-3">
                      <span className="mr-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold">
                        Why {i + 1}
                      </span>
                      {w}
                    </li>
                  ))}
                </ol>
                <div className="mt-3 rounded-lg border border-success/40 bg-success/10 p-3 text-xs text-success">
                  {record.rootCause.verification}
                </div>
              </div>
            </div>
          )}
          {tab === "root-cause" && !record.rootCause.statement && (
            <PendingSection
              title="Root cause"
              note="Root cause analysis has not started. This section stays empty until 5-Why / Ishikawa work is verified — Broadview does not pre-fill it."
            />
          )}

          {tab === "corrective" && record.correctiveActions.length > 0 && (
            <div>
              <SectionTitle
                icon={Wrench}
                title="Containment, corrective and preventive actions"
                sub="D3 / D5 / D7 traceability"
              />
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full min-w-[860px] text-left text-xs">
                  <thead className="bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">ID</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Action</th>
                      <th className="px-3 py-2">Owner</th>
                      <th className="px-3 py-2">Due</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Evidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {record.correctiveActions.map((a) => (
                      <tr key={a.id} className="border-t border-border align-top">
                        <td className="px-3 py-2 font-medium">{a.id}</td>
                        <td className="px-3 py-2 text-muted-foreground">{a.type}</td>
                        <td className="max-w-sm px-3 py-2">{a.action}</td>
                        <td className="px-3 py-2 text-muted-foreground">{a.owner}</td>
                        <td className="px-3 py-2 text-muted-foreground">{a.due}</td>
                        <td className="px-3 py-2">
                          <span
                            className={`rounded border px-1.5 py-0.5 text-[10px] ${
                              a.status === "Complete"
                                ? "border-success/40 bg-success/10 text-success"
                                : a.status === "In Progress"
                                  ? "border-primary/40 bg-primary/10 text-primary"
                                  : "border-border text-muted-foreground"
                            }`}
                          >
                            {a.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{a.evidence}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {tab === "corrective" && !(record.correctiveActions.length > 0) && (
            <PendingSection
              title="Containment, corrective and preventive actions"
              note="No containment or corrective actions have been logged yet."
            />
          )}

          {tab === "eco" && record.engineeringChanges.length > 0 && (
            <div>
              <SectionTitle
                icon={GitBranch}
                title="Engineering change"
                sub="Design, spec, process and supplier changes released from D5/D6"
              />
              <div className="grid gap-3 lg:grid-cols-3">
                {record.engineeringChanges.map((e) => (
                  <div
                    key={e.id}
                    className="rounded-lg border border-border bg-background p-4 text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{e.id}</span>
                      <span
                        className={`ml-auto rounded border px-1.5 py-0.5 text-[10px] ${
                          e.status === "Released"
                            ? "border-success/40 bg-success/10 text-success"
                            : "border-primary/40 bg-primary/10 text-primary"
                        }`}
                      >
                        {e.status}
                      </span>
                    </div>
                    <div className="mt-1 font-medium">{e.title}</div>
                    <p className="mt-2 text-muted-foreground">{e.detail}</p>
                    <dl className="mt-3 space-y-1 text-[11px] text-muted-foreground">
                      <div className="flex justify-between">
                        <dt>Level</dt>
                        <dd className="text-foreground">{e.changeLevel}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt>Parts</dt>
                        <dd className="text-foreground">{e.affectedParts}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt>PPAP</dt>
                        <dd className="text-foreground">{e.ppapLevel}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt>Owner</dt>
                        <dd className="text-foreground">{e.owner}</dd>
                      </div>
                      {e.releasedOn && (
                        <div className="flex justify-between">
                          <dt>Released</dt>
                          <dd className="text-foreground">{e.releasedOn}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tab === "eco" && !(record.engineeringChanges.length > 0) && (
            <PendingSection
              title="Engineering change"
              note="No engineering change has been raised for this record yet."
            />
          )}

          {tab === "pfmea" && (
            <div>
              <SectionTitle
                icon={ShieldCheck}
                title="PFMEA & Control Plan"
                sub={`Sourced from the existing quality register for ${record.linkedCaseId}`}
              />
              {pfmea ? (
                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="space-y-2 rounded-lg border border-border bg-background p-4 text-xs">
                    <Row l="Process" v={pfmea.process} />
                    <Row l="Failure mode" v={pfmea.failureMode} />
                    <Row l="Effect" v={pfmea.effect} />
                    <Row l="Cause (D4)" v={pfmea.cause} />
                    <Row l="Corrective (D5)" v={pfmea.corrective} />
                    <Row l="Control method" v={pfmea.control} />
                    <Row l="Frequency" v={pfmea.frequency} />
                    <Row l="Reaction plan" v={pfmea.reaction} />
                    <Row l="Owner" v={pfmea.owner} />
                  </div>
                  <div className="rounded-lg border border-border bg-background p-4">
                    <div className="text-xs font-semibold">Risk reduction</div>
                    <div className="mt-3 grid grid-cols-2 gap-3 text-center text-xs">
                      <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4">
                        <div className="text-muted-foreground">RPN before</div>
                        <div className="text-2xl font-bold text-destructive">
                          {pfmea.sevBefore * pfmea.occBefore * pfmea.detBefore}
                        </div>
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          S{pfmea.sevBefore} · O{pfmea.occBefore} · D{pfmea.detBefore}
                        </div>
                      </div>
                      <div className="rounded-lg border border-success/40 bg-success/10 p-4">
                        <div className="text-muted-foreground">RPN after</div>
                        <div className="text-2xl font-bold text-success">
                          {pfmea.sevAfter * pfmea.occAfter * pfmea.detAfter}
                        </div>
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          S{pfmea.sevAfter} · O{pfmea.occAfter} · D{pfmea.detAfter}
                        </div>
                      </div>
                    </div>
                    <p className="mt-3 text-[11px] text-muted-foreground">
                      Severity is unchanged by design — the failure effect remains safety-relevant.
                      Risk reduction is achieved through occurrence (supplier SPC gate) and
                      detection (100% in-line screening).
                    </p>
                  </div>
                </div>
              ) : (
                <PendingSection
                  title="PFMEA & Control Plan"
                  note="No PFMEA row is linked yet. The control-plan linkage is created when root cause and corrective action are confirmed."
                />
              )}
            </div>
          )}

          {tab === "validation" && record.validation.length > 0 && (
            <div>
              <SectionTitle
                icon={Beaker}
                title="Validation"
                sub="Evidence that the corrective action works before closure"
              />
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full min-w-[900px] text-left text-xs">
                  <thead className="bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">ID</th>
                      <th className="px-3 py-2">Test</th>
                      <th className="px-3 py-2">Method</th>
                      <th className="px-3 py-2">Sample</th>
                      <th className="px-3 py-2">Acceptance</th>
                      <th className="px-3 py-2">Result</th>
                      <th className="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {record.validation.map((v) => (
                      <tr key={v.id} className="border-t border-border align-top">
                        <td className="px-3 py-2 font-medium">{v.id}</td>
                        <td className="px-3 py-2">{v.test}</td>
                        <td className="px-3 py-2 text-muted-foreground">{v.method}</td>
                        <td className="px-3 py-2 text-muted-foreground">{v.sample}</td>
                        <td className="px-3 py-2 text-muted-foreground">{v.acceptance}</td>
                        <td className="px-3 py-2">{v.result}</td>
                        <td className="px-3 py-2">
                          <span
                            className={`rounded border px-1.5 py-0.5 text-[10px] ${
                              v.status === "Pass"
                                ? "border-success/40 bg-success/10 text-success"
                                : v.status === "Fail"
                                  ? "border-destructive/40 bg-destructive/10 text-destructive"
                                  : "border-primary/40 bg-primary/10 text-primary"
                            }`}
                          >
                            {v.status}
                          </span>
                          {v.completedOn && (
                            <div className="mt-1 text-[10px] text-muted-foreground">
                              {v.completedOn}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {tab === "validation" && !(record.validation.length > 0) && (
            <PendingSection
              title="Validation"
              note="No validation protocols have been defined yet. Validation opens once a corrective action is released."
            />
          )}

          {tab === "effectiveness" && record.effectiveness.length > 0 && (
            <div>
              <SectionTitle
                icon={Activity}
                title="Effectiveness"
                sub="Field-verified confirmation that the loop is closing"
              />
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {record.effectiveness.map((e) => (
                  <div
                    key={e.metric}
                    className="rounded-lg border border-border bg-background p-4 text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{e.metric}</span>
                      <span
                        className={`ml-auto rounded border px-1.5 py-0.5 text-[10px] ${
                          e.status === "On Target"
                            ? "border-success/40 bg-success/10 text-success"
                            : e.status === "Improving"
                              ? "border-primary/40 bg-primary/10 text-primary"
                              : "border-warning/40 bg-warning/10 text-warning"
                        }`}
                      >
                        {e.status}
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className="text-[10px] text-muted-foreground">Baseline</div>
                        <div className="font-semibold">{e.baseline}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-muted-foreground">Current</div>
                        <div className="font-semibold text-primary">{e.current}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-muted-foreground">Target</div>
                        <div className="font-semibold">{e.target}</div>
                      </div>
                    </div>
                    <div className="mt-2 text-[10px] text-muted-foreground">{e.window}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tab === "effectiveness" && !(record.effectiveness.length > 0) && (
            <PendingSection
              title="Effectiveness"
              note="Effectiveness monitoring opens once corrective actions are in the field."
            />
          )}

          {tab === "read-across" && (
            <div>
              <SectionTitle
                icon={Lightbulb}
                title="Lessons learned & read-across"
                sub="Propagating prevention to sibling programs"
              />
              {ra ? (
                <>
                  <div className="rounded-lg border border-primary/40 bg-primary/10 p-4 text-xs">
                    <div className="font-semibold text-primary">Lesson learned</div>
                    <p className="mt-1">{ra.lesson}</p>
                    <div className="mt-2 text-[11px] text-muted-foreground">
                      Common denominator: {ra.rootCauseTag}
                    </div>
                  </div>
                  <div className="mt-3 overflow-x-auto rounded-lg border border-border">
                    <table className="w-full min-w-[700px] text-left text-xs">
                      <thead className="bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2">Target product</th>
                          <th className="px-3 py-2">Program</th>
                          <th className="px-3 py-2">Owner</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2">Action due</th>
                          <th className="px-3 py-2">Risk avoided</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ra.targets.map((t) => (
                          <tr key={t.product} className="border-t border-border">
                            <td className="px-3 py-2 font-medium">{t.product}</td>
                            <td className="px-3 py-2 text-muted-foreground">{t.program}</td>
                            <td className="px-3 py-2 text-muted-foreground">{t.owner}</td>
                            <td className="px-3 py-2">
                              <span
                                className={`rounded border px-1.5 py-0.5 text-[10px] ${
                                  t.status === "Applied"
                                    ? "border-success/40 bg-success/10 text-success"
                                    : t.status === "In Review"
                                      ? "border-primary/40 bg-primary/10 text-primary"
                                      : "border-warning/40 bg-warning/10 text-warning"
                                }`}
                              >
                                {t.status}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">{t.actionDue}</td>
                            <td className="px-3 py-2">{t.riskAvoided}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <PendingSection
                  title="Lessons learned & read-across"
                  note="Read-across opens once a verified root cause exists that can be propagated to sibling programs."
                />
              )}
            </div>
          )}

          {tab === "audit" && (
            <div>
              <SectionTitle
                icon={FolderOpen}
                title="Audit / activity trail"
                sub="IATF 16949 and ISO 9001:2015 clause-mapped record"
              />
              <ol className="space-y-2">
                {record.activity.map((a, i) => (
                  <li
                    key={i}
                    className="flex gap-3 rounded-lg border border-border bg-background p-3 text-xs"
                  >
                    <div className="w-32 shrink-0 text-muted-foreground">{a.ts}</div>
                    <div className="flex-1">
                      <div>{a.action}</div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">
                        {a.actor} · {a.clause}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </section>
      </main>

      {stageKey && (
        <StageWorkspaceDialog
          record={record}
          stageKey={stageKey}
          onClose={() => setStageKey(null)}
        />
      )}
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <span className="rounded border border-border bg-background px-2 py-1">
      <span className="text-muted-foreground">{label}: </span>
      {value}
    </span>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-background p-4 text-xs">
      <div className="mb-2 text-sm font-semibold">{title}</div>
      {children}
    </div>
  );
}

function Row({ l, v }: { l: string; v: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border/60 py-1 last:border-0">
      <span className="text-muted-foreground">{l}</span>
      <span className="text-right font-medium">{v}</span>
    </div>
  );
}

function SectionTitle({
  icon: Icon,
  title,
  sub,
}: {
  icon: typeof Activity;
  title: string;
  sub: string;
}) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Icon className="h-4 w-4 text-primary" /> {title}
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>
    </div>
  );
}

function NoRecord({ id }: { id: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="max-w-lg rounded-xl border border-border bg-card p-8 text-center">
        <AlertTriangle className="mx-auto h-8 w-8 text-warning" />
        <h1 className="mt-4 text-lg font-semibold">No Problem Record for “{id}”</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This record is not in the registry. Records are created from a ranked Top 5 issue, opening
          a draft at Stage 1 — Warranty Signal, pre-populated with the issue's claims, SPC signal
          and attribution.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <Link
            to="/problem-records"
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Open Problem Record Registry
          </Link>
          <Link
            to="/top5"
            className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Back to Top 5
          </Link>
        </div>
      </div>
    </div>
  );
}

function PendingSection({ title, note }: { title: string; note: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-background p-8 text-center">
      <Clock className="mx-auto h-6 w-6 text-muted-foreground" />
      <div className="mt-3 text-sm font-semibold">{title} — Pending, not yet completed</div>
      <p className="mx-auto mt-1 max-w-xl text-xs text-muted-foreground">{note}</p>
    </div>
  );
}
